/**
 * Hook para dados unificados do modal premium de cliente
 * Busca contadores e métricas em uma única query RPC otimizada
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-10
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('CustomerModalData');

/**
 * Interface dos dados retornados pela RPC
 */
export interface CustomerModalData {
  counters: {
    total_deals: number;
    won_deals: number;
    open_deals: number;
    conversations: number;
    contracts: number;
    documents: number;
    /** v1.1.0: tarefas vinculadas ao contato principal (não concluídas/canceladas) */
    tasks: number;
    /** v1.3.0: veículos ativos do cliente/contato (módulo de Veículos) */
    vehicles: number;
    /** v1.4.0: propostas dos negócios do cliente/contato (exceto canceladas) */
    proposals: number;
  };

  metrics: {
    total_value: number;
    win_rate: number;
  };
}

/**
 * Opções do hook para controle de lazy loading
 */
interface CustomerModalDataOptions {
  enabled?: boolean;
}

/**
 * Hook para buscar dados unificados do modal de cliente
 * Usa RPC get_customer_modal_data para uma única query otimizada
 * 
 * @param customerId - ID do cliente
 * @param options - Opções de configuração (enabled para lazy loading)
 */
export function useCustomerModalData(
  customerId: string | null,
  options?: CustomerModalDataOptions,
  /**
   * v1.2.0: modo Contato — quando não há cliente vinculado, os contadores
   * (negócios, conversas e tarefas) são resolvidos pelo contato na mesma RPC.
   */
  contactId?: string | null
) {
  const { currentTenant } = useAuth();

  // Determina se query deve executar (cliente OU contato)
  const isEnabled =
    (options?.enabled ?? true) && (!!customerId || !!contactId) && !!currentTenant?.id;

  return useQuery({
    queryKey: ['customer-modal-data', customerId, contactId ?? null],
    queryFn: async (): Promise<CustomerModalData> => {
      if ((!customerId && !contactId) || !currentTenant?.id) {
        return {
          counters: { total_deals: 0, won_deals: 0, open_deals: 0, conversations: 0, contracts: 0, documents: 0, tasks: 0, vehicles: 0, proposals: 0 },
          metrics: { total_value: 0, win_rate: 0 },
        };
      }

      logger.debug('Buscando dados do modal:', { customerId, contactId });

      // Chama RPC unificada (suporta cliente ou contato)
      const { data, error } = await (supabase.rpc as any)('get_customer_modal_data', {
        p_customer_id: customerId,
        p_tenant_hint: currentTenant.id,
        p_contact_id: customerId ? null : contactId ?? null,
      });

      if (error) {
        logger.error('Erro ao buscar dados do modal:', error.message);
        throw error;
      }

      // RPC retorna array, pegamos primeiro item
      const result = Array.isArray(data) ? data[0] : data;
      
      if (!result) {
        return {
          counters: { total_deals: 0, won_deals: 0, open_deals: 0, conversations: 0, contracts: 0, documents: 0, tasks: 0, vehicles: 0, proposals: 0 },
          metrics: { total_value: 0, win_rate: 0 },
        };
      }

      // Calcula win rate
      const totalDeals = Number(result.total_deals) || 0;
      const wonDeals = Number(result.won_deals) || 0;
      const winRate = totalDeals > 0 ? Math.round((wonDeals / totalDeals) * 100) : 0;

      logger.debug('Dados do modal carregados:', {
        total_deals: totalDeals,
        won_deals: wonDeals,
        conversations: result.conversations,
      });

      return {
        counters: {
          total_deals: totalDeals,
          won_deals: wonDeals,
          open_deals: Number(result.open_deals) || 0,
          conversations: Number(result.conversations) || 0,
          contracts: Number(result.contracts) || 0,
          documents: Number(result.documents) || 0,
          // v1.1.0: tarefas vinculadas
          tasks: Number(result.tasks) || 0,
          // v1.3.0: veículos ativos
          vehicles: Number(result.vehicles) || 0,
          // v1.4.0: propostas (badge da aba Propostas)
          proposals: Number(result.proposals) || 0,
        },
        metrics: {
          total_value: Number(result.total_value) || 0,
          win_rate: winRate,
        },
      };
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // Cache de 30 segundos
  });
}
